import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

const F = ({ id, label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type} />
  </div>
);

const LifeInsuranceForm = ({ details, onChange }) => {
  const set = (k, v) => onChange({ ...details, [k]: v });
  const beneficiaries = details.beneficiaries || [];

  const setB = (idx, key, val) => {
    const next = [...beneficiaries];
    next[idx] = { ...next[idx], [key]: val };
    set('beneficiaries', next);
  };

  return (
    <div className="space-y-4" data-testid="life-insurance-form">
      <div className="grid grid-cols-2 gap-3">
        <F id="ca-li-insurer" label="Insurer" value={details.insurer_name} onChange={(v) => set('insurer_name', v)} placeholder="e.g. AIG" />
        <div>
          <Label htmlFor="ca-li-type">Policy type</Label>
          <Select value={details.policy_type || ''} onValueChange={(v) => set('policy_type', v)}>
            <SelectTrigger id="ca-li-type" data-testid="ca-li-type"><SelectValue placeholder="Pick one" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="term">Term</SelectItem>
              <SelectItem value="whole">Whole life</SelectItem>
              <SelectItem value="universal">Universal</SelectItem>
              <SelectItem value="variable">Variable</SelectItem>
              <SelectItem value="group">Group / employer</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F id="ca-li-number" label="Policy number" value={details.policy_number} onChange={(v) => set('policy_number', v)} placeholder="e.g. AIG-987654" />
        <F id="ca-li-renewal" label="Renewal / expiry date" value={details.renewal_date} onChange={(v) => set('renewal_date', v)} placeholder="YYYY-MM-DD" type="date" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F id="ca-li-premium" label="Premium amount" value={details.premium_amount} onChange={(v) => set('premium_amount', v ? Number(v) : null)} placeholder="0" type="number" />
        <div>
          <Label htmlFor="ca-li-freq">Premium frequency</Label>
          <Select value={details.premium_frequency || ''} onValueChange={(v) => set('premium_frequency', v)}>
            <SelectTrigger id="ca-li-freq" data-testid="ca-li-freq"><SelectValue placeholder="Pick one" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annually">Annually</SelectItem>
              <SelectItem value="single">Single premium (paid up)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F id="ca-li-broker" label="Broker / agent name" value={details.broker_name} onChange={(v) => set('broker_name', v)} placeholder="e.g. Marsh McLennan" />
        <F id="ca-li-brokerc" label="Broker contact" value={details.broker_contact} onChange={(v) => set('broker_contact', v)} placeholder="email or phone" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Beneficiaries</Label>
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => set('beneficiaries', [...beneficiaries, { name: '', relationship: '', percentage: null }])}
            data-testid="ca-li-add-beneficiary"
          >
            <Plus className="w-4 h-4 mr-1" />Add beneficiary
          </Button>
        </div>
        {beneficiaries.length === 0 && (
          <p className="text-xs text-slate-500">No beneficiaries added yet.</p>
        )}
        {beneficiaries.map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5"><Input placeholder="Name" value={b.name || ''} onChange={(e) => setB(i, 'name', e.target.value)} /></div>
            <div className="col-span-4"><Input placeholder="Relationship" value={b.relationship || ''} onChange={(e) => setB(i, 'relationship', e.target.value)} /></div>
            <div className="col-span-2"><Input type="number" placeholder="%" value={b.percentage ?? ''} onChange={(e) => setB(i, 'percentage', e.target.value ? Number(e.target.value) : null)} /></div>
            <div className="col-span-1">
              <Button type="button" variant="ghost" size="icon"
                onClick={() => set('beneficiaries', beneficiaries.filter((_, j) => j !== i))}
                data-testid={`ca-li-del-beneficiary-${i}`}
              ><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LifeInsuranceForm;
